import {
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
} from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { MatDialog } from "@angular/material/dialog";
import { ReaderService } from "../../services/reader.service";
import { ImageGalleryComponent } from "../image-gallery/image-gallery.component";
import { ErrorHandlerDialogComponent } from "../error-handler-dialog/error-handler-dialog.component";
import { NewVehicleComponent } from "../new-vehicle/new-vehicle.component";
import { Vehicle } from "../utility/utility-classes";
import { AuthenticationService } from "../authentication/authentication/authentication.service";
import { WriterService } from "../../services/writer.service";
import Quill from "quill";
import { QuillTableEmbed } from "../base-components/quill-table-embed";
import { Observable, forkJoin } from "rxjs";
import { convertUtcToEst } from "../utility/time-conversion.util";
import { VehicleVersionComponent } from "../vehicle-version/vehicle-version.component";
import * as FileSaver from "file-saver";

@Component({
    selector: "app-vehicle",
    templateUrl: "./vehicle.component.html",
    styleUrls: ["./vehicle.component.scss"],
    standalone: false
})
export class VehicleComponent implements OnInit, OnDestroy {
  public editHighlight: boolean;
  public editPowertrain: boolean;
  convertUtcToEst = convertUtcToEst;

  constructor(
    private route: ActivatedRoute,
    private readerService: ReaderService,
    private dialog: MatDialog,
    public authService: AuthenticationService,
    private writerService: WriterService,
    private cdr: ChangeDetectorRef
  ) {
    Quill.register(QuillTableEmbed, true);
  }
  isLoading = true;
  isLoadingAttachments = false;

  vehicle: Vehicle = new Vehicle();
  photoUrls: {
    front?: string;
    rear?: string;
    interior?: string;
    side?: string;
  } = {};
  private objectUrls: string[] = [];
  attachments: any;
  notEditing: boolean;

  quillConfiguration = {
    toolbar: false,
    clipboard: {
      matchVisual: false,
    },
  };

  highlight = {
    id: "",
    title: "",
    content: "",
    user: "",
  };

  powertrain = {
    id: "",
    title: "",
    content: "",
    user: "",
  };
  ngOnInit() {
    this.isLoading = true;
    this.loadData().subscribe({
      complete: () => {
        this.isLoading = false;
      },
    });
  }

  ngOnDestroy(): void {
    this.revokePhotoUrls();
  }

  private revokePhotoUrls(): void {
    this.objectUrls.forEach((url) => {
      if (typeof URL !== "undefined" && typeof URL.revokeObjectURL === "function") {
        URL.revokeObjectURL(url);
      }
    });
    this.objectUrls = [];
    this.photoUrls = {};
  }

  private loadAuthenticatedPhoto(filename: string, key: keyof VehicleComponent["photoUrls"]): void {
    if (!filename) {
      return;
    }
    this.readerService.getAuthenticatedImageUrl(filename).subscribe({
      next: (url) => {
        this.objectUrls.push(url);
        this.photoUrls[key] = url;
        this.cdr.detectChanges();
      },
      error: () => {
        // Leave this photo slot empty if the authenticated fetch fails.
      },
    });
  }

  private loadPhotoUrls(): void {
    this.revokePhotoUrls();
    if (!this.vehicle) {
      return;
    }
    this.loadAuthenticatedPhoto(this.vehicle.photofronturl, "front");
    this.loadAuthenticatedPhoto(this.vehicle.photorearurl, "rear");
    this.loadAuthenticatedPhoto(this.vehicle.photointeriorurl, "interior");
    this.loadAuthenticatedPhoto(this.vehicle.photosideurl, "side");
  }

  private loadData(): Observable<any> {
    this.vehicle = undefined;

    const observables: Observable<any>[] = [];
    this.route.params.subscribe({
      next: (response: any) => {
        const getVehicleDataCall = this.readerService.getVehicleData(
          response.id
        );
        observables.push(getVehicleDataCall);
        getVehicleDataCall.subscribe({
          next: (result) => {
            this.vehicle = result;
            result.highlights =
              result.highlights == null ? "" : result.highlights;
            result.powertrain =
              result.powertrain == null ? "" : result.powertrain;
            this.highlight.content = result.highlights;
            this.powertrain.content = result.powertrain;
            this.loadPhotoUrls();
            this.cdr.detectChanges();
          },
          error: (error) => {
            this.dialog.open(ErrorHandlerDialogComponent, {
              data: {
                message:
                  "There was a problem attempting to access this" +
                    " record. Please try again. " +
                    error.error ==
                  null
                    ? error.message
                    : error.error.message,
                status: error.error == null ? error.status : error.error.status,
              },
            });
          },
        });
        const getVehicleAttachmentsCall =
          this.readerService.getVehicleAttachments(response.id);
        observables.push(getVehicleAttachmentsCall);
        getVehicleAttachmentsCall.subscribe((result) => {
          this.attachments = result;
          this.cdr.detectChanges();
        });
      },
      error: (error) => {
        this.dialog.open(ErrorHandlerDialogComponent, {
          data: {
            message:
              "There was a problem attempting to access this" +
                " record's attachments. Please try again. " +
                error.error ==
              null
                ? error.message
                : error.error.message,
            status: error.error == null ? error.status : error.error.status,
          },
        });
      },
    });

    return forkJoin(observables);
  }

  openImageGallery(images: string[]) {
    images = images.filter((image) => image != null);
    if (images.length > 0) {
      this.dialog.open(ImageGalleryComponent, {
        data: { images },
      });
    } else {
      alert("No images to display");
    }
  }

  openDialogForVehicle(veh: Vehicle) {
    const dialogRef = this.dialog.open(NewVehicleComponent, {
      data: veh,
      autoFocus: false,
    });
    dialogRef.afterClosed().subscribe({
      complete: () => {
        this.loadData();
      },
    });
  }

  openDialogForVehicleVersion(veh: Vehicle) {
    const dialogRef = this.dialog.open(VehicleVersionComponent, {
      data: veh,
    });
    dialogRef.afterClosed().subscribe({
      complete: () => {},
    });
  }

  edit() {
    this.openDialogForVehicle(this.vehicle);
  }

  duplicate() {
    const duplicate: Vehicle = JSON.parse(JSON.stringify(this.vehicle));
    duplicate.id = null;
    duplicate.yearbegin = null;
    duplicate.yearend = null;
    duplicate.highlights = "";
    duplicate.powertrain = "";
    duplicate.photofront = null;
    duplicate.photorear = null;
    duplicate.photoside = null;
    duplicate.photointerior = null;
    duplicate.fphotofront = null;
    duplicate.fphotorear = null;
    duplicate.fphotoside = null;
    duplicate.fphotointerior = null;
    duplicate.attachment = null;
    this.openDialogForVehicle(duplicate);
  }

  uploadFile(fileInputEvent: any) {
    const maxFileSize = 1024 * 1024 * 1024;
    if (fileInputEvent.target.files[0].size > maxFileSize) {
      this.dialog.open(ErrorHandlerDialogComponent, {
        data: {
          message:
            "File size is too large. Please reduce the file size and try again.",
          status: "Error",
        },
      });
      return;
    }
    this.vehicle.attachment = fileInputEvent.target.files[0];
    this.isLoadingAttachments = true;
    const createdby = this.authService.userInfo.userName;
    const created = new Date().toISOString();
    this.writerService
      .uploadFiles(this.vehicle, "attachments", createdby, created)
      .subscribe(
        () => {
          this.readerService
            .getVehicleAttachments(this.vehicle.id)
            .subscribe((result) => {
              this.attachments = result;
              this.cdr.detectChanges();
            });
          this.isLoadingAttachments = false;
        },
        (error) => {
          switch (error.status) {
            case 413:
              this.dialog.open(ErrorHandlerDialogComponent, {
                data: {
                  message:
                    "Error saving file: " +
                    fileInputEvent.target.files[0].name +
                    " because it is too large. Please reduce the file size and try again.",
                  status:
                    error.error == null ? error.status : error.error.status,
                },
              });
              break;
            default:
              this.dialog.open(ErrorHandlerDialogComponent, {
                data: {
                  message:
                    "Error saving file: " +
                    fileInputEvent.target.files[0].name +
                    " because it already exists. Please rename the file and try again.",
                  status:
                    error.error == null ? error.status : error.error.status,
                },
              });
              this.isLoadingAttachments = false;
          }
        }
      );
  }

  deleteFile(element: any) {
    this.writerService.deleteFile(element.filename, "attachments").subscribe(
      () => {
        this.dialog.open(ErrorHandlerDialogComponent, {
          data: { message: "File deleted successfully", status: "Success" },
        });
        this.attachments = this.attachments.filter(
          (record) => record.filename !== element.filename
        );
      },
      (error) => {
        this.dialog.open(ErrorHandlerDialogComponent, {
          data: {
            message: "Delete failed. Please refresh and try again",
            status: error.error == null ? error.status : error.error.status,
          },
        });
      }
    );
  }

  downloadAttachment(element: any, event?: Event): void {
    if (event) {
      event.preventDefault();
    }
    this.readerService.getAttachment(element.filename).subscribe({
      next: (blob) => {
        FileSaver.saveAs(blob, element.originalfilename || element.filename);
      },
      error: (error) => {
        this.dialog.open(ErrorHandlerDialogComponent, {
          data: {
            message: "Download failed. Please try again.",
            status: error.error == null ? error.status : error.error.status,
          },
        });
      },
    });
  }

  decodeURL(filename: string) {
    return decodeURIComponent(filename);
  }

  encodeURL(filename: string) {
    return encodeURIComponent(filename);
  }

  editorCreated(quill: Quill) {
    quill.clipboard.addMatcher("TABLE", (node, delta) => {
      const Delta = Quill.import("delta");
      const tableTagStyles = node.getAttribute("style");
      return new Delta([
        {
          insert: {
            QuillTableEmbed:
              `<style>#tableId {${tableTagStyles} margin: 0 auto !important; }</style>` +
              delta.ops[0].insert.QuillTableEmbed,
          },
        },
      ]);
    });
  }
}
